/**
 * No Organization Selected Prompt Component
 *
 * Reusable component displayed when user is authenticated but has no organization selected.
 * This prevents RLS errors on pages that require org context (Connections, etc.).
 * Encourages user to switch to an organization with friendly messaging.
 *
 * Usage:
 * ```tsx
 * // In any page that requires org context
 * const { data: user } = useCurrentUser()
 *
 * if (!user?.currentOrganization) {
 *   return <NoOrgSelectedPrompt featureName="connections" />
 * }
 * ```
 */

import { Button, Space, Typography } from 'antd'
import { TeamOutlined, SwapOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

const { Title, Paragraph } = Typography

/**
 * Props for NoOrgSelectedPrompt component
 */
export interface NoOrgSelectedPromptProps {
  /**
   * Name of the feature that requires org context (used in description)
   * @default "this feature"
   */
  featureName?: string

  /** Custom style */
  style?: React.CSSProperties

  /** Custom class name */
  className?: string
}

/**
 * No Organization Selected Prompt
 *
 * Friendly message shown when user needs to select an organization
 * before they can access a feature. Uses welcoming, non-technical
 * language similar to other zero-state components.
 *
 * @example
 * ```tsx
 * // In ConnectionsPage
 * if (!user?.currentOrganization) {
 *   return <NoOrgSelectedPrompt featureName="connections" />
 * }
 *
 * // In OfferingsPage
 * if (!user?.currentOrganization) {
 *   return <NoOrgSelectedPrompt featureName="offerings" />
 * }
 * ```
 */
export const NoOrgSelectedPrompt: React.FC<NoOrgSelectedPromptProps> = ({
  featureName = 'this feature',
  style,
  className,
}) => {
  const navigate = useNavigate()

  const handleSwitchOrg = () => {
    // Navigate to org switch page
    navigate('/switch-org')
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '24px',
        ...style,
      }}
      className={className}
    >
      <Space direction="vertical" size="large" align="center">
        {/* Icon */}
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #faad14 0%, #fa8c16 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TeamOutlined style={{ fontSize: '40px', color: '#fff' }} />
        </div>

        {/* Heading */}
        <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
          Select an Organization
        </Title>

        {/* Description */}
        <Paragraph
          type="secondary"
          style={{
            textAlign: 'center',
            maxWidth: '400px',
            fontSize: '15px',
            margin: 0,
          }}
        >
          {featureName.charAt(0).toUpperCase() + featureName.slice(1)} are managed at the
          organization level. Please select an organization to continue.
        </Paragraph>

        {/* CTA Button */}
        <Button
          type="primary"
          size="large"
          icon={<SwapOutlined />}
          onClick={handleSwitchOrg}
          style={{
            marginTop: '8px',
            paddingLeft: '32px',
            paddingRight: '32px',
          }}
        >
          Switch Organization
        </Button>

        {/* Info box */}
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '8px',
            maxWidth: '450px',
          }}
        >
          <Paragraph
            type="secondary"
            style={{
              fontSize: '13px',
              margin: 0,
              textAlign: 'center',
            }}
          >
            You can switch organizations anytime using the <strong>Account</strong> menu in the
            header.
          </Paragraph>
        </div>
      </Space>
    </div>
  )
}

export default NoOrgSelectedPrompt
